import {
  parseCreditAccountTransaction,
  toLegacyOperations,
} from "@gearbox-protocol/sdk/history";
import { getChain, OnchainSDK } from "@gearbox-protocol/sdk/onchain";
import { useAsync } from "react-use";
import type { AsyncState } from "react-use/lib/useAsyncFn";
import {
  type Abi,
  type Address,
  createPublicClient,
  decodeEventLog,
  type Hex,
  http,
} from "viem";

import logger from "../../../logger";
import { useUnpausedForkStatus } from "../context";
import addExtraAbis from "./addExtraAbis";
import { buildParseInput } from "./findCreditContext";
import type {
  CreditAccountOperation as CreditAccountOperationModel,
  LegacyApiOperation,
} from "./model";
import { CreditSessionOperationParser } from "./model";
import type { ProtocolsList } from "./protocols";
import { loadProtocols } from "./protocols";
import type { ParsedTxEvent } from "./types";

export interface ParsedTxResult {
  ops: CreditAccountOperationModel[];
  legacyOps: LegacyApiOperation[];
  protocols: ProtocolsList;
  underlying: Address;
  creditAccount: Address;
  creditFacade: Address;
  tokens: Record<Address, { addr: Address; symbol: string; decimals: number }>;
  blockNumber: bigint;
  events: ParsedTxEvent[];
}

export function useParseTx(txHash: Hex): AsyncState<ParsedTxResult> {
  const {
    marketConfigurators,
    rwaFactories,
    rpcUrl,
    addressProvider,
    network,
  } = useUnpausedForkStatus();

  return useAsync(async () => {
    if (!marketConfigurators?.length) {
      throw new Error("No market configurators found");
    }

    const chain = getChain(network);
    const client = createPublicClient({
      chain,
      transport: http(rpcUrl, { timeout: 480_000 }),
    });

    const receipt = await client.getTransactionReceipt({
      hash: txHash,
    });
    const block = await client.getBlock({ blockNumber: receipt.blockNumber });
    const trace = await client.request({
      method: "debug_traceTransaction" as any,
      params: [txHash, { tracer: "callTracer" }] as any,
    } as any);

    // viem doesn't always populate blockTimestamp on receipts; fill it from the block.
    // The history parser only Numbers() the field, so the type can stay loose.
    const receiptWithTs = {
      ...receipt,
      blockTimestamp: block.timestamp,
    };

    const sdk = new OnchainSDK(
      network,
      { rpcURLs: [rpcUrl], timeout: 480_000 },
      { logger },
    );
    await sdk.attach({
      addressProvider,
      marketConfigurators,
      rwaFactories,
      blockNumber: receipt.blockNumber,
      ignoreUpdateablePrices: true,
    });
    await addExtraAbis(sdk);
    const protocols = await loadProtocols(chain.id).catch(err => {
      logger.warn({ err }, "failed to load protocols list");
      return {} as ProtocolsList;
    });

    const input = buildParseInput(sdk, receiptWithTs, trace);
    const ops = parseCreditAccountTransaction(input);
    const legacyOps = toLegacyOperations(ops, {
      sessionId: input.creditAccount,
    });
    const modelOps = CreditSessionOperationParser.deserializeArray(
      legacyOps as any,
    );

    const tokens: Record<
      Address,
      { addr: Address; symbol: string; decimals: number }
    > = {};
    for (const t of sdk.tokensMeta.values()) {
      tokens[t.addr.toLowerCase() as Address] = {
        addr: t.addr,
        symbol: t.symbol,
        decimals: t.decimals,
      };
    }

    const events: ParsedTxEvent[] = receipt.logs.map(log => {
      const contract = sdk.getContract<Abi>(log.address);
      if (!contract || !("abi" in contract)) {
        return {
          decoded: false,
          address: log.address,
          logIndex: log.logIndex,
          topics: log.topics,
          data: log.data,
        };
      }
      try {
        const decoded = decodeEventLog({
          abi: contract.abi as Abi,
          data: log.data,
          topics: log.topics as [Hex, ...Hex[]],
        });
        if (!decoded.eventName) {
          return {
            decoded: false,
            address: log.address,
            logIndex: log.logIndex,
            topics: log.topics,
            data: log.data,
          };
        }
        return {
          decoded: true,
          address: log.address,
          contractName: contract.name || undefined,
          eventName: decoded.eventName,
          args: (decoded.args ?? {}) as
            | Record<string, unknown>
            | readonly unknown[],
          logIndex: log.logIndex,
          topics: log.topics,
          data: log.data,
        };
      } catch (err) {
        return {
          decoded: false,
          address: log.address,
          logIndex: log.logIndex,
          topics: log.topics,
          data: log.data,
          error: err instanceof Error ? err.message : String(err),
        };
      }
    });

    return {
      ops: modelOps,
      legacyOps,
      protocols,
      underlying: input.underlying,
      creditAccount: input.creditAccount,
      creditFacade: input.creditFacade,
      tokens,
      blockNumber: receipt.blockNumber,
      events,
    };
  }, [txHash, addressProvider, marketConfigurators, rpcUrl, network]);
}
